import type {
  Context,
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
} from 'react'
import { createContext, useState } from 'react'
import createContextStateHook from '~utils/create-hook-for-context-state'

export interface AlertProps {
  variant: 'success' | 'warning' | 'error' | 'info'
  text: string
  headline?: string
  className?: string
}

export type AnimatedAlertState = {
  delay?: number
  isVisible: boolean
  setIsVisible: Dispatch<SetStateAction<boolean>>
  setStatus: (
    value: ReactNode,
    delay?: number,
    newVariant?: AlertVariant,
  ) => void
  setText: Dispatch<SetStateAction<ReactNode>>
  setVariant: Dispatch<SetStateAction<AlertVariant>>
  text: ReactNode
  variant: AlertVariant
}

export type AlertVariant = AlertProps['variant']

export type TSetStatus = (
  value: ReactNode,
  delay?: number,
  variant?: AlertVariant,
) => void

export const AnimatedAlertContext = createContext<AnimatedAlertState | null>(
  null,
) as Context<AnimatedAlertState>

export default function AnimatedAlertProvider({ children }: PropsWithChildren) {
  const [text, setText] = useState<ReactNode>('')
  const [variant, setVariant] = useState<AlertVariant>('success')
  const [isVisible, setIsVisible] = useState(false)
  const [delay, setDelay] = useState<number>()

  const setStatus: TSetStatus = (value, delay = 5000, newVariant) => {
    setText(value)
    setVariant(newVariant || variant)
    setIsVisible(true)
    setDelay(delay)
    setTimeout(() => {
      setText('')
      setIsVisible(false)
    }, delay)
  }

  return (
    <AnimatedAlertContext.Provider
      value={{
        setText,
        setVariant,
        setIsVisible,
        setStatus,
        isVisible,
        variant,
        delay,
        text,
      }}
    >
      {children}
    </AnimatedAlertContext.Provider>
  )
}

export const useAnimatedAlert = createContextStateHook(
  AnimatedAlertContext,
  'AnimatedAlertContext',
)
